import logo from '@/static/assets/images/logo_with_text.svg';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, message } from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { CoreInput } from '@/components/Form/InputAntForm';
import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';
import { AuthenticationBackground } from '../components/AuthenticationBackground';
import { authService, authErrorCodes } from '../services/auth.service';

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords do not match'),
});

export const UserResetPassword = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) {
      message.error('Invalid or expired password reset link');
      navigate('/login');
    }
  }, [mode, oobCode, navigate]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (input: ResetPasswordForm) => {
    if (!oobCode) return;
    setIsSubmitting(true);
    try {
      await authService.resetPassword(oobCode, input.newPassword);
      message.success('Password reset successfully. Please log in with your new password.');
      navigate('/login');
    } catch (error: any) {
      const errorMessages: Record<string, string> = {
        [authErrorCodes.expiredActionCode]: 'This reset link has expired. Please request a new one.',
        [authErrorCodes.invalidActionCode]: 'This reset link is invalid. Please request a new one.',
      };
      message.error(errorMessages[error.code] ?? getErrorMessage(error) ?? 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonStyle = {
    backgroundColor: '#cc1e1e',
    borderColor: '#cc1e1e',
    borderRadius: '8px',
    height: '40px',
    fontSize: '20px',
    fontWeight: 600,
  };

  const inputStyle = {
    borderRadius: '8px',
    height: '40px',
  };

  return (
    <div className='flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 relative'>
      <AuthenticationBackground />
      <div className='w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md z-100 relative'>
        <div className='flex justify-center mb-6'>
          <img src={logo} alt='MindX Logo' className='h-12' />
        </div>
        <h1 className='text-2xl font-bold text-center'>Reset Password</h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CoreInput
            control={control}
            name='newPassword'
            label='New Password'
            type='password'
            required
            placeholder='Enter new password'
            style={inputStyle}
          />
          <CoreInput
            control={control}
            name='confirmPassword'
            label='Confirm Password'
            type='password'
            required
            placeholder='Confirm new password'
            style={inputStyle}
          />
          <Button
            type='primary'
            htmlType='submit'
            className='w-full mt-4 rounded-lg'
            style={buttonStyle}
            loading={isSubmitting}
          >
            Reset Password
          </Button>
          <div className='text-center mt-4'>
            <a href='#' className='text-blue-500 text-sm' onClick={() => navigate('/login')}>
              Back to login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserResetPassword;
